import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash2, Copy, XCircle, LinkIcon, CheckCircle, DollarSign, Users,
  Loader2, Clock, Target, TrendingUp, CreditCard, Award, FileText, Calendar,
  ChevronDown, Download, Filter,
} from "lucide-react";

const AdminPaymentLinks = () => {
  const { API, token } = useAuth();
  const [tab, setTab] = useState("links");
  const [links, setLinks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [balances, setBalances] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkTab, setLinkTab] = useState("active");

  // Dialogs
  const [createLinkOpen, setCreateLinkOpen] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [createBDPOpen, setCreateBDPOpen] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Forms
  const [linkForm, setLinkForm] = useState({ client_name: "", client_email: "", engagement_type: "one_time", agent_code: "", notes: "" });
  const [lineItems, setLineItems] = useState([{ description: "", amount: "" }]);
  const [planForm, setPlanForm] = useState({ client_name: "", client_email: "", description: "", total_amount: "", agent_code: "", notes: "", num_installments: 2 });
  const [installments, setInstallments] = useState([{ amount: "", due_date: "" }, { amount: "", due_date: "" }]);
  const [bdpForm, setBdpForm] = useState({ name: "", email: "", password: "" });
  const [adhocForm, setAdhocForm] = useState({ agent_code: "", amount: "", description: "" });

  // Earnings filters
  const [earningsAgent, setEarningsAgent] = useState("");
  const [earningsStart, setEarningsStart] = useState("");
  const [earningsEnd, setEarningsEnd] = useState("");

  const h = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [linksR, agentsR, balR] = await Promise.all([
        fetch(`${API}/bdp/payment-links`, { headers: h }),
        fetch(`${API}/bdp/admin/agents`, { headers: h }),
        fetch(`${API}/bdp/admin/outstanding-balances`, { headers: h }),
      ]);
      if (linksR.ok) setLinks(await linksR.json());
      if (agentsR.ok) setAgents(await agentsR.json());
      if (balR.ok) setBalances(await balR.json());
    } catch { // error handled silently
      console.error("Request failed:", error);
    }
    setLoading(false);
  };

  const fetchEarnings = async () => {
    const params = new URLSearchParams();
    if (earningsAgent) params.set("agent_code", earningsAgent);
    if (earningsStart) params.set("start_date", earningsStart);
    if (earningsEnd) params.set("end_date", earningsEnd);
    try {
      const r = await fetch(`${API}/bdp/admin/earnings-summary?${params}`, { headers: h });
      if (r.ok) setEarnings(await r.json());
    } catch { // error handled silently
      console.error("Request failed:", error);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (tab === "earnings") fetchEarnings(); }, [tab, earningsAgent, earningsStart, earningsEnd]);

  const total = lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);

  // Payment plan installment helpers
  const updateInstallmentCount = (n) => {
    const num = Math.max(2, Math.min(12, parseInt(n) || 2));
    setPlanForm({ ...planForm, num_installments: num });
    const newInst = Array.from({ length: num }, (_, i) => installments[i] || { amount: "", due_date: "" });
    setInstallments(newInst);
  };

  const splitEvenly = () => {
    const t = parseFloat(planForm.total_amount);
    if (!t) return;
    const each = Math.floor(t / installments.length * 100) / 100;
    const remainder = Math.round((t - each * installments.length) * 100) / 100;
    setInstallments(installments.map((inst, i) => ({
      ...inst,
      amount: i === 0 ? (each + remainder).toFixed(2) : each.toFixed(2),
    })));
  };

  // Handlers
  const handleCreateLink = async () => {
    if (!linkForm.client_name || !linkForm.client_email || !linkForm.agent_code) { toast.error("Fill required fields"); return; }
    if (lineItems.some(li => !li.description || !li.amount)) { toast.error("Fill all line items"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/bdp/payment-links`, {
        method: "POST", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ ...linkForm, line_items: lineItems.map(li => ({ description: li.description, amount: parseFloat(li.amount) })) }),
      });
      const d = await r.json();
      if (r.ok) { toast.success("Payment link created"); setCreateLinkOpen(false); setLineItems([{ description: "", amount: "" }]); setLinkForm({ client_name: "", client_email: "", engagement_type: "one_time", agent_code: "", notes: "" }); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleCreatePlan = async () => {
    if (!planForm.client_name || !planForm.client_email || !planForm.agent_code || !planForm.total_amount || !planForm.description) {
      toast.error("Fill required fields"); return;
    }
    if (installments.some(i => !i.amount || !i.due_date)) { toast.error("Fill all installment amounts and due dates"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/bdp/admin/payment-plan`, {
        method: "POST", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: planForm.client_name, client_email: planForm.client_email,
          description: planForm.description, total_amount: parseFloat(planForm.total_amount),
          agent_code: planForm.agent_code, notes: planForm.notes,
          installments: installments.map((inst, i) => ({ amount: parseFloat(inst.amount), due_date: inst.due_date, description: inst.description || `Payment ${i + 1}` })),
        }),
      });
      const d = await r.json();
      if (r.ok) { toast.success(`Payment plan created - ${d.total_installments} installments`); setCreatePlanOpen(false); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleCreateBDP = async () => {
    if (!bdpForm.name || !bdpForm.email || !bdpForm.password) { toast.error("Fill all fields"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/bdp/admin/agents`, {
        method: "POST", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify(bdpForm),
      });
      const d = await r.json();
      if (r.ok) { toast.success(`Agent created: ${d.agent_code}`); setCreateBDPOpen(false); setBdpForm({ name: "", email: "", password: "" }); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleAdhoc = async () => {
    if (!adhocForm.agent_code || !adhocForm.amount || !adhocForm.description) { toast.error("Fill all fields"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/bdp/admin/adhoc-commission`, {
        method: "POST", headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ ...adhocForm, amount: parseFloat(adhocForm.amount) }),
      });
      const d = await r.json();
      if (r.ok) { toast.success(d.message); setAdhocOpen(false); setAdhocForm({ agent_code: "", amount: "", description: "" }); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    setCreating(false);
  };

  const handleSettle = async (planId) => {
    if (!window.confirm("Mark this balance as settled?")) return;
    try {
      const r = await fetch(`${API}/bdp/admin/outstanding-balances/${planId}/settle`, { method: "POST", headers: h });
      if (r.ok) { toast.success("Settled"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleCancelLink = async (linkId) => {
    if (!window.confirm("Void this payment link?")) return;
    try {
      const r = await fetch(`${API}/bdp/payment-links/${linkId}/cancel`, { method: "POST", headers: h });
      if (r.ok) { toast.success("Cancelled"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleMarkPaid = async (linkId) => {
    if (!window.confirm("Manually mark this link as paid?")) return;
    try {
      const r = await fetch(`${API}/bdp/payment-links/${linkId}/mark-paid`, { method: "POST", headers: h });
      if (r.ok) { toast.success("Marked as paid"); fetchAll(); }
      else { const d = await r.json(); toast.error(d.detail || "Failed"); }
    } catch { toast.error("Failed"); }
  };

  const handleCreateInstallmentLink = async (planId, number) => {
    try {
      const r = await fetch(`${API}/bdp/admin/payment-plan/${planId}/create-installment-link?installment_number=${number}`, { method: "POST", headers: h });
      const d = await r.json();
      if (r.ok) { toast.success("Installment link created"); navigator.clipboard.writeText(d.stripe_url); toast.success("Link copied to clipboard"); fetchAll(); }
      else toast.error(d.detail || "Failed");
    } catch { toast.error("Failed"); }
  };

  const filteredLinks = links.filter(l => l.status === linkTab);
  const activeBalances = balances.filter(b => b.status !== "settled");
  const settledBalances = balances.filter(b => b.status === "settled");

  const statusBadge = (s) => {
    switch(s) {
      case "active": return <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Outstanding</Badge>;
      case "paid": return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Paid</Badge>;
      case "cancelled": return <Badge className="bg-gray-100 text-gray-500 text-[10px]">Cancelled</Badge>;
      default: return <Badge variant="outline" className="text-[10px]">{s}</Badge>;
    }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-payment-links">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]">Sales Management</h1>
            <p className="text-sm text-gray-500">Payment links, agents, commissions & payroll</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setCreateBDPOpen(true)} variant="outline" size="sm" className="gap-1.5" data-testid="create-bdp-btn"><Users className="w-3.5 h-3.5" /> New Agent</Button>
            <Button onClick={() => setAdhocOpen(true)} variant="outline" size="sm" className="gap-1.5" data-testid="adhoc-btn"><Award className="w-3.5 h-3.5" /> Ad-hoc Commission</Button>
            <Button onClick={() => { setCreatePlanOpen(true); setInstallments([{ amount: "", due_date: "" }, { amount: "", due_date: "" }]); setPlanForm({ ...planForm, num_installments: 2 }); }} variant="outline" size="sm" className="gap-1.5" data-testid="create-plan-btn"><CreditCard className="w-3.5 h-3.5" /> Payment Plan</Button>
            <Button onClick={() => setCreateLinkOpen(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="admin-create-link-btn"><Plus className="w-3.5 h-3.5" /> Payment Link</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-gray-100 h-10">
            <TabsTrigger value="links" className="gap-1.5"><LinkIcon className="w-3.5 h-3.5" /> Payment Links</TabsTrigger>
            <TabsTrigger value="plans" className="gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Payment Plans ({activeBalances.length})</TabsTrigger>
            <TabsTrigger value="agents" className="gap-1.5"><Users className="w-3.5 h-3.5" /> Agents ({agents.length})</TabsTrigger>
            <TabsTrigger value="earnings" className="gap-1.5"><FileText className="w-3.5 h-3.5" /> Earnings & Payroll</TabsTrigger>
            <TabsTrigger value="leaderboard" className="gap-1.5"><Target className="w-3.5 h-3.5" /> Leaderboards</TabsTrigger>
          </TabsList>

          {/* PAYMENT LINKS TAB */}
          <TabsContent value="links" className="space-y-4 mt-4">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {["active", "paid", "cancelled"].map(s => (
                <button key={s} onClick={() => setLinkTab(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${linkTab === s ? "bg-white shadow-sm text-[#1A2636]" : "text-gray-500"}`}>
                  {s === "active" ? "Outstanding" : s.charAt(0).toUpperCase() + s.slice(1)} ({links.filter(l => l.status === s).length})
                </button>
              ))}
            </div>
            {filteredLinks.length ? filteredLinks.map(link => (
              <Card key={link.link_id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{link.client_name}</span>
                      {statusBadge(link.status)}
                      <Badge variant="outline" className="text-[10px]">{link.engagement_type === "monthly" ? "Monthly" : "One-time"}</Badge>
                      <span className="text-[10px] text-gray-400 font-mono">Agent: {link.agent_code}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{link.line_items?.map(li => li.description).join(" · ")}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Created {new Date(link.created_at).toLocaleDateString()}{link.paid_at && ` · Paid ${new Date(link.paid_at).toLocaleDateString()}`}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold">${Number(link.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                      {link.commission_amount > 0 && <p className="text-[11px] text-emerald-600">Comm: ${Number(link.commission_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>}
                    </div>
                    {link.status === "active" && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(link.stripe_url); toast.success("Copied"); }} title="Copy link"><Copy className="w-3.5 h-3.5" /></Button>
                        <Button variant="outline" size="sm" className="text-emerald-600" onClick={() => handleMarkPaid(link.link_id)} title="Mark as paid"><CheckCircle className="w-3.5 h-3.5" /></Button>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleCancelLink(link.link_id)} title="Void"><XCircle className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )) : <div className="text-center py-12 text-gray-400 text-sm"><LinkIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />No {linkTab} links</div>}
          </TabsContent>

          {/* PAYMENT PLANS TAB */}
          <TabsContent value="plans" className="space-y-4 mt-4">
            <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wider">Active Plans</h3>
            {activeBalances.length ? activeBalances.map(b => (
              <Card key={b.plan_id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-sm">{b.client_name}</span>
                        <Badge className={b.status === "active" ? "bg-amber-50 text-amber-700 text-[10px]" : "bg-blue-50 text-blue-700 text-[10px]"}>
                          {b.status === "deposit_paid" ? "Deposit Paid" : "Active"}
                        </Badge>
                        <span className="text-[10px] text-gray-400 font-mono">Agent: {b.agent_code}</span>
                      </div>
                      <p className="text-xs text-gray-500">{b.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Total: ${Number(b.total_amount).toLocaleString()} · Paid: ${Number(b.amount_paid || 0).toLocaleString()} · <span className="font-semibold text-amber-600">Remaining: ${Number(b.remaining_balance).toLocaleString()}</span></p>

                      {/* Installment schedule */}
                      {b.installments?.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {b.installments.map((inst, idx) => (
                            <div key={inst.label || inst.description || i} className={`flex items-center justify-between text-xs px-3 py-2 rounded-md ${inst.status === "paid" ? "bg-emerald-50" : inst.status === "active" ? "bg-amber-50" : "bg-gray-50"}`}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{inst.description || `Payment ${inst.number}`}</span>
                                <Badge className={`text-[9px] ${inst.status === "paid" ? "bg-emerald-100 text-emerald-700" : inst.status === "active" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                                  {inst.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-gray-500">Due: {new Date(inst.due_date + "T12:00:00").toLocaleDateString()}</span>
                                <span className="font-semibold">${Number(inst.amount).toLocaleString()}</span>
                                {inst.status === "pending" && !inst.link_id && (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => handleCreateInstallmentLink(b.plan_id, inst.number)}>
                                    <LinkIcon className="w-3 h-3" /> Create Link
                                  </Button>
                                )}
                                {inst.stripe_url && inst.status !== "paid" && (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => { navigator.clipboard.writeText(inst.stripe_url); toast.success("Copied"); }}>
                                    <Copy className="w-3 h-3" /> Copy
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handleSettle(b.plan_id)} className="bg-emerald-600 hover:bg-emerald-700 gap-1 shrink-0" data-testid={`settle-${b.plan_id}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Settle
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )) : <div className="text-center py-8 text-gray-400 text-sm">No active payment plans</div>}

            {settledBalances.length > 0 && (
              <>
                <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mt-6">Settled</h3>
                {settledBalances.map(b => (
                  <div key={b.plan_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm opacity-60">
                    <span>{b.client_name} - {b.description}</span>
                    <span className="font-mono">${Number(b.total_amount).toLocaleString()}</span>
                  </div>
                ))}
              </>
            )}
          </TabsContent>

          {/* AGENTS TAB */}
          <TabsContent value="agents" className="space-y-3 mt-4">
            {agents.map(a => (
              <Card key={a.user_id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{a.name}</p>
                    <p className="text-xs text-gray-500">{a.email}</p>
                  </div>
                  <span className="font-mono text-sm bg-emerald-50 text-emerald-700 px-3 py-1 rounded-md">{a.agent_code}</span>
                </CardContent>
              </Card>
            ))}
            {!agents.length && <div className="text-center py-12 text-gray-400 text-sm">No agents yet</div>}
          </TabsContent>

          {/* EARNINGS & PAYROLL TAB */}
          <TabsContent value="earnings" className="space-y-5 mt-4">
            {/* Filters */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-48">
                    <Label className="text-xs">Agent</Label>
                    <Select value={earningsAgent || "all"} onValueChange={v => setEarningsAgent(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="All Agents" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Agents</SelectItem>
                        {agents.map(a => <SelectItem key={a.agent_code} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" value={earningsStart} onChange={e => setEarningsStart(e.target.value)} className="h-9 w-40" />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input type="date" value={earningsEnd} onChange={e => setEarningsEnd(e.target.value)} className="h-9 w-40" />
                  </div>
                  <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => { setEarningsAgent(""); setEarningsStart(""); setEarningsEnd(""); }}>
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Totals summary */}
            {earnings?.totals && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Total Sales", value: `$${earnings.totals.total_sales.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, icon: DollarSign, c: "from-emerald-500 to-teal-600" },
                  { label: "Total Commission", value: `$${earnings.totals.total_commission.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, icon: TrendingUp, c: "from-blue-500 to-indigo-600" },
                  { label: "Total Hours", value: `${earnings.totals.total_hours.toFixed(1)}h`, icon: Clock, c: "from-purple-500 to-violet-600" },
                  { label: "Active Agents", value: earnings.totals.total_agents, icon: Users, c: "from-amber-500 to-orange-600" },
                ].map((s, i) => (
                  <Card key={s.key || s.name || s.title || i} className="border-0 shadow-sm overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex items-center gap-3 p-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.c} flex items-center justify-center`}>
                          <s.icon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400">{s.label}</p>
                          <p className="text-lg font-bold text-[#1A2636]">{s.value}</p>
                        </div>
                      </div>
                      <div className={`h-0.5 bg-gradient-to-r ${s.c}`} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Per-agent breakdown */}
            {earnings?.agents?.map(a => (
              <Card key={a.agent_code} className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-[#1A2636]">{a.name}</h3>
                      <p className="text-xs text-gray-400">{a.email} · <span className="font-mono">{a.agent_code}</span></p>
                    </div>
                    <div className="flex gap-4 text-right text-sm">
                      <div><p className="text-[10px] text-gray-400">Sales</p><p className="font-bold">${a.total_sales.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p></div>
                      <div><p className="text-[10px] text-gray-400">Commission</p><p className="font-bold text-emerald-600">${a.total_commission.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p></div>
                      <div><p className="text-[10px] text-gray-400">Hours</p><p className="font-bold">{a.total_hours.toFixed(1)}h</p></div>
                      <div><p className="text-[10px] text-gray-400">Deals</p><p className="font-bold">{a.deals_closed}</p></div>
                    </div>
                  </div>
                  {(a.deals.length > 0 || a.adhoc_items.length > 0) && (
                    <div className="border-t pt-3 space-y-1.5">
                      {a.deals.map(d => (
                        <div key={d.link_id} className="flex items-center justify-between text-xs py-1">
                          <div><span className="font-medium">{d.client_name}</span> <Badge className="ml-1 bg-emerald-50 text-emerald-700 text-[9px]">{d.engagement_type === "monthly" ? "Monthly" : "One-time"}</Badge></div>
                          <div className="flex gap-4"><span>${d.amount.toLocaleString()}</span><span className="text-emerald-600 font-medium">${d.commission.toLocaleString()}</span></div>
                        </div>
                      ))}
                      {a.adhoc_items.map((ad, i) => (
                        <div key={ad.title || ad.name || ad.label || i} className="flex items-center justify-between text-xs py-1">
                          <div><span className="font-medium">{ad.description}</span> <Badge className="ml-1 bg-purple-50 text-purple-700 text-[9px]">Bonus</Badge></div>
                          <span className="text-purple-600 font-medium">${ad.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {(!earnings?.agents?.length) && <div className="text-center py-8 text-gray-400 text-sm">No earnings data found</div>}
          </TabsContent>

          {/* LEADERBOARDS TAB */}
          <TabsContent value="leaderboard" className="mt-4">
            <LeaderboardTab API={API} token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* ===== DIALOGS ===== */}

      {/* Create Payment Link */}
      <Dialog open={createLinkOpen} onOpenChange={setCreateLinkOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Payment Link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Client Name *</Label><Input value={linkForm.client_name} onChange={e => setLinkForm({...linkForm, client_name: e.target.value})} /></div>
              <div><Label className="text-xs">Client Email *</Label><Input type="email" value={linkForm.client_email} onChange={e => setLinkForm({...linkForm, client_email: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Engagement Type</Label>
                <Select value={linkForm.engagement_type} onValueChange={v => setLinkForm({...linkForm, engagement_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="one_time">One-Time (20%)</SelectItem><SelectItem value="monthly">Monthly (15%)</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Sales Agent *</Label>
                <Select value={linkForm.agent_code} onValueChange={v => setLinkForm({...linkForm, agent_code: v})}>
                  <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent>{agents.map(a => <SelectItem key={a.agent_code} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs">Line Items</Label><Button variant="ghost" size="sm" onClick={() => setLineItems([...lineItems, {description:"",amount:""}])} className="h-6 text-xs text-emerald-600"><Plus className="w-3 h-3" /> Add</Button></div>
              {lineItems.map((li, i) => (
                <div key={li.description || i} className="flex gap-2 mb-2">
                  <Input value={li.description} onChange={e => { const items = [...lineItems]; items[i].description = e.target.value; setLineItems(items); }} placeholder="Description" className="flex-1" />
                  <Input type="number" value={li.amount} onChange={e => { const items = [...lineItems]; items[i].amount = e.target.value; setLineItems(items); }} placeholder="Amount" className="w-28" />
                  {lineItems.length > 1 && <Button variant="ghost" size="sm" onClick={() => setLineItems(lineItems.filter((_,idx)=>idx!==i))} className="text-red-400 px-2"><Trash2 className="w-3 h-3" /></Button>}
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm flex justify-between"><span>Total</span><span className="font-bold">${total.toFixed(2)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateLinkOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLink} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Plan - Installments */}
      <Dialog open={createPlanOpen} onOpenChange={setCreatePlanOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Payment Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Client Name *</Label><Input value={planForm.client_name} onChange={e => setPlanForm({...planForm, client_name: e.target.value})} /></div>
              <div><Label className="text-xs">Client Email *</Label><Input type="email" value={planForm.client_email} onChange={e => setPlanForm({...planForm, client_email: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">Description *</Label><Input value={planForm.description} onChange={e => setPlanForm({...planForm, description: e.target.value})} placeholder="e.g. EHR Implementation" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Total Amount *</Label><Input type="number" value={planForm.total_amount} onChange={e => setPlanForm({...planForm, total_amount: e.target.value})} /></div>
              <div>
                <Label className="text-xs">Sales Agent *</Label>
                <Select value={planForm.agent_code} onValueChange={v => setPlanForm({...planForm, agent_code: v})}>
                  <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent>{agents.map(a => <SelectItem key={a.agent_code} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div>
                <Label className="text-xs">Number of Payments</Label>
                <Input type="number" min={2} max={12} value={planForm.num_installments} onChange={e => updateInstallmentCount(e.target.value)} className="w-24" />
              </div>
              <Button variant="outline" size="sm" className="mt-5 text-xs" onClick={splitEvenly}>Split Evenly</Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Installment Schedule</Label>
              {installments.map((inst, i) => (
                <div key={inst.label || inst.description || i} className="flex gap-2 items-center">
                  <span className="text-xs text-gray-400 w-6 shrink-0">#{i+1}</span>
                  <Input type="number" value={inst.amount} onChange={e => { const arr = [...installments]; arr[i].amount = e.target.value; setInstallments(arr); }} placeholder="Amount" className="w-28" />
                  <Input type="date" value={inst.due_date} onChange={e => { const arr = [...installments]; arr[i].due_date = e.target.value; setInstallments(arr); }} className="flex-1" />
                </div>
              ))}
              {planForm.total_amount && (
                <p className="text-xs text-gray-400">
                  Installments total: ${installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0).toFixed(2)} of ${parseFloat(planForm.total_amount || 0).toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePlanOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePlan} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create BDP Agent */}
      <Dialog open={createBDPOpen} onOpenChange={setCreateBDPOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Sales Agent</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name *</Label><Input value={bdpForm.name} onChange={e => setBdpForm({...bdpForm, name: e.target.value})} data-testid="bdp-name" /></div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={bdpForm.email} onChange={e => setBdpForm({...bdpForm, email: e.target.value})} data-testid="bdp-email" /></div>
            <div><Label className="text-xs">Password *</Label><Input type="password" value={bdpForm.password} onChange={e => setBdpForm({...bdpForm, password: e.target.value})} data-testid="bdp-password" /></div>
            <p className="text-xs text-gray-400">A unique agent code will be auto-generated.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateBDPOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateBDP} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bdp-submit">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Agent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad-hoc Commission */}
      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ad-hoc Commission</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Sales Agent *</Label>
              <Select value={adhocForm.agent_code} onValueChange={v => setAdhocForm({...adhocForm, agent_code: v})}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>{agents.map(a => <SelectItem key={a.agent_code} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Amount ($) *</Label><Input type="number" value={adhocForm.amount} onChange={e => setAdhocForm({...adhocForm, amount: e.target.value})} /></div>
            <div><Label className="text-xs">Description *</Label><Input value={adhocForm.description} onChange={e => setAdhocForm({...adhocForm, description: e.target.value})} placeholder="e.g. Q1 bonus" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocOpen(false)}>Cancel</Button>
            <Button onClick={handleAdhoc} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Commission"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// Leaderboard sub-component
const LeaderboardTab = ({ API, token }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/bdp/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setData);
  }, [API, token]);

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {[
        { title: "Total Sales Volume", data: data.by_sales, key: "total_sales", fmt: v => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0 })}`, icon: DollarSign },
        { title: "Closed Deals", data: data.by_deals, key: "deals_closed", fmt: v => `${v} deals`, icon: Target },
      ].map((lb, idx) => (
        <Card key={lb.title || lb.name || lb.label || idx} className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <lb.icon className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-bold text-[#1A2636]">{lb.title}</h3>
            </div>
            {lb.data?.length ? lb.data.map((e, i) => (
              <div key={e.agent_code} className={`flex items-center justify-between p-3 rounded-lg mb-1 ${i < 3 ? "bg-emerald-50/50" : "bg-gray-50"}`}>
                <div className="flex items-center gap-3">
                  <span className="w-6 text-center font-bold text-sm">{i === 0 ? "1" : i === 1 ? "2" : i === 2 ? "3" : `${i+1}`}</span>
                  <div>
                    <span className="text-sm font-medium">{e.name}</span>
                    <p className="text-[10px] text-gray-400 font-mono">{e.agent_code}</p>
                  </div>
                </div>
                <span className="font-bold text-sm">{lb.fmt(e[lb.key])}</span>
              </div>
            )) : <p className="text-sm text-gray-400 text-center py-6">No data</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminPaymentLinks;
